import { useEffect } from "react";
import { createPortal } from "react-dom";

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** Text on the confirm button while loading (defaults to "<confirmLabel>..."). */
  loadingLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal = ({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  loadingLabel,
  onConfirm,
  onCancel,
}: ConfirmModalProps) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !loading) onCancel();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onCancel, loading]);

  return createPortal(
    <div
      className="fixed inset-0 z-[10009999] flex items-center justify-center bg-black/40 px-16"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="bg-white rounded-[20px] p-28 flex flex-col gap-20 w-full max-w-400 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-8">
          <h3 className="text-[18px] font-semibold text-(--color-bold-heading) font-poppins">
            {title}
          </h3>
          <p className="text-[14px] text-(--color-heading) leading-20">
            {message}
          </p>
        </div>

        <div className="flex items-center justify-end gap-10">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-20 py-10 rounded-full border border-(--color-border) text-[14px] text-(--color-heading) hover:bg-[#F5F5F5] transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            aria-busy={loading}
            className="px-20 py-10 rounded-full bg-red-500 text-white text-[14px] font-medium hover:bg-red-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 inline-flex items-center gap-8"
          >
            {loading ? (
              <>
                <span aria-hidden="true" className="h-14 w-14 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                {loadingLabel ?? `${confirmLabel}...`}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmModal;
